<template>
  <section class="mt-2">
    <h3>Analyse de gestion</h3>
    <p class="text-muted">Ces indicateurs portent sur vos données autorisées. Ils ne représentent pas nécessairement toute l’entreprise et peuvent évoluer avec vos affectations.</p>
    <b-card>
      <b-form @submit.prevent="applyFilters">
        <b-row class="align-items-end">
          <b-col md="3">
            <b-form-group label="Période" label-for="dashboard-period">
              <b-form-select id="dashboard-period" v-model="mode" :options="modes" />
            </b-form-group>
          </b-col>
          <b-col v-if="mode === 'year'" md="3">
            <b-form-group label="Année" label-for="dashboard-year">
              <b-form-input id="dashboard-year" v-model="year" type="number" min="1900" max="9998" required />
            </b-form-group>
          </b-col>
          <template v-if="mode === 'range'">
            <b-col md="3"><b-form-group label="Du" label-for="dashboard-from">
              <b-form-input id="dashboard-from" v-model="from" type="date" :max="to || '9998-12-31'" required />
            </b-form-group></b-col>
            <b-col md="3"><b-form-group label="Au" label-for="dashboard-to">
              <b-form-input id="dashboard-to" v-model="to" type="date" :min="from" max="9998-12-31" required />
            </b-form-group></b-col>
          </template>
          <b-col md="3"><b-button class="mb-1" type="submit" variant="primary" :disabled="loading">Appliquer</b-button></b-col>
        </b-row>
      </b-form>
      <p class="text-muted mb-0">La conversion porte sur la création des offres ; les gains et les soldes portent sur la date de gain des projets. Le reste à encaisser correspond aux budgets réels TTC moins les encaissements TTC reçus à ce jour, même après la période sélectionnée. Les budgets vendus ou manquants sont exclus des soldes.</p>
      <b-alert :show="!!validationError" variant="warning" class="mt-1">{{ validationError }}</b-alert>
    </b-card>
    <div v-if="loading" class="text-center p-3" role="status"><b-spinner label="Chargement des analyses" /></div>
    <b-alert v-else-if="error" show variant="danger">{{ error }} <b-button variant="link" @click="fetchAnalytics">Réessayer</b-button></b-alert>
    <template v-else-if="report">
      <p class="text-muted">{{ periodLabel }} · Données au {{ formatDate(report.as_of) }}</p>
      <b-alert v-if="report.data_quality.projects_without_won_at > 0" show variant="warning">
        {{ report.data_quality.projects_without_won_at }} projet(s) accessible(s) sans date de gain.
        Les périodes filtrées les excluent des soldes et des gains ; les soldes courants les incluent si leur budget est réel.
        Sans filtre, leurs soldes figurent sous « Date de gain inconnue ».
      </b-alert>
      <b-row>
        <b-col md="6"><b-card title="Reste à encaisser TTC sur les budgets réels"><h3>{{ money(report.payments.outstanding_amount) }}</h3><span>{{ report.payments.projects_count }} projets</span></b-card></b-col>
        <b-col md="6"><b-card title="Conversion des offres de la période"><h3>{{ rate(report.conversion.period.rate) }}</h3><span>{{ report.conversion.period.won }} gagnées / {{ report.conversion.period.total }} offres créées</span><p class="mb-0 mt-1">{{ report.conversion.period.closed_without_followup }} clôturées sans suite</p></b-card></b-col>
      </b-row>
      <b-row>
        <b-col lg="6"><b-card title="Conversion cumulée depuis l’origine">
          <h3>{{ rate(report.conversion.all_time.rate) }}</h3>
          <p>{{ report.conversion.all_time.won }} gagnées / {{ report.conversion.all_time.total }} offres créées · {{ report.conversion.all_time.closed_without_followup }} clôturées sans suite</p>
          <p class="text-muted mb-0">Inclut toutes les années, indépendamment de la période choisie.</p>
        </b-card></b-col>
        <b-col lg="6"><b-card title="Situation actuelle — toutes périodes">
          <p>{{ report.current.open_offers }} offres en cours · {{ report.current.won_offers }} gagnées · {{ report.current.lost_offers }} perdues · {{ report.current.closed_without_followup_offers }} clôturées sans suite</p>
          <p class="mb-0">Reste à encaisser TTC : {{ money(report.current.outstanding_amount) }}</p>
        </b-card></b-col>
      </b-row>
      <b-row>
        <b-col lg="6"><b-card title="Reste à encaisser TTC par mois de gain des projets">
          <vue-apex-charts v-if="report.payments.by_month.length" type="bar" height="280" :options="paymentOptions" :series="paymentSeries" />
          <p v-else class="text-muted">Aucun solde restant pour cette période.</p>
        </b-card></b-col>
        <b-col lg="6"><b-card title="Projets gagnés pendant la période">
          <vue-apex-charts v-if="report.wins_by_month.length" type="bar" height="280" :options="winsOptions" :series="winsSeries" />
          <p v-else class="text-muted">Aucun projet gagné pendant cette période.</p>
          <small class="text-muted">Les offres peuvent avoir été créées au cours d’une année précédente.</small>
        </b-card></b-col>
      </b-row>
      <b-card title="Conversion par année de création des offres">
        <b-table :items="report.conversion.by_year" :fields="conversionFields" responsive show-empty empty-text="Aucune offre pour cette période.">
          <template #cell(rate)="data">{{ rate(data.item.rate) }}</template>
        </b-table>
        <small class="text-muted">Statuts actuels des offres créées dans la période sélectionnée. Pour une plage partielle, seules les dates sélectionnées sont incluses. Une offre créée en 2025 et gagnée en 2026 contribue à la conversion de 2025.</small>
      </b-card>
      <b-card :title="`Projets avec des paiements en attente (${report.payments.projects.total})`">
        <b-table :items="report.payments.projects.data" :fields="paymentFields" responsive show-empty empty-text="Aucun paiement en attente pour cette période.">
          <template #cell(code)="data">
            <b-link v-if="$can('read_project', 'Projets')" :to="{name: 'projects-view', params: {id: data.item.uid}}">{{ data.item.code }}</b-link>
            <span v-else>{{ data.item.code }}</span>
          </template>
          <template #cell(outstanding_amount)="data">{{ money(data.item.outstanding_amount) }}</template>
          <template #cell(budget_amount)="data">{{ money(data.item.budget_amount) }}</template>
          <template #cell(received_amount)="data">{{ money(data.item.received_amount) }}</template>
        </b-table>
        <b-pagination v-if="report.payments.projects.total > 10" v-model="page" :total-rows="report.payments.projects.total" :per-page="10" @input="fetchAnalytics" />
      </b-card>
    </template>
  </section>
</template>
<script>
import {BCard, BRow, BCol, BForm, BFormGroup, BFormSelect, BFormInput, BButton, BAlert, BSpinner, BTable, BLink, BPagination} from 'bootstrap-vue';
import VueApexCharts from 'vue-apexcharts';
import dashboardService from '@/store/services/dashboard-service';
import {dashboardErrorMessage, formatDashboardAmount, isDashboardDate} from './dashboard-utils';

export default {
  components: {BCard, BRow, BCol, BForm, BFormGroup, BFormSelect, BFormInput, BButton, BAlert, BSpinner, BTable, BLink, BPagination, VueApexCharts},
  data() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const today = `${currentYear}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return {
      currentYear, today, mode: 'year', year: currentYear, from: `${currentYear}-01-01`, to: today,
      applied: {year: currentYear}, page: 1, loading: false, error: '', validationError: '', report: null, requestId: 0,
      modes: [{value: 'year', text: 'Par année'}, {value: 'range', text: 'Plage de dates'}, {value: 'all', text: 'Toutes les périodes'}],
      conversionFields: [{key: 'year', label: 'Année'}, {key: 'total', label: 'Offres'}, {key: 'won', label: 'Gagnées'}, {key: 'lost', label: 'Perdues'}, {key: 'open', label: 'En cours'}, {key: 'closed_without_followup', label: 'Clôturées sans suite'}, {key: 'rate', label: 'Conversion'}],
      paymentFields: [{key: 'code', label: 'Projet'}, {key: 'customer_name', label: 'Client'}, {key: 'budget_amount', label: 'Budget TTC'}, {key: 'received_amount', label: 'Encaissements TTC'}, {key: 'outstanding_amount', label: 'Reste à encaisser TTC'}],
    };
  },
  computed: {
    periodLabel() { return this.applied.year ? `Année ${this.applied.year}` : this.applied.from ? `Du ${this.applied.from} au ${this.applied.to}` : 'Toutes les périodes'; },
    paymentOptions() { return this.chartOptions(this.report.payments.by_month.map(item => item.month === null ? 'Date de gain inconnue' : item.month)); },
    paymentSeries() { return [
      {name: 'Reste à encaisser TTC (FCFA)', data: this.report.payments.by_month.map(item => Number(item.outstanding_amount))},
    ]; },
    winsOptions() { return this.chartOptions(this.report.wins_by_month.map(item => item.month)); },
    winsSeries() { return [{name: 'Projets gagnés', data: this.report.wins_by_month.map(item => item.count)}]; },
  },
  created() { this.fetchAnalytics(); },
  beforeDestroy() { this.requestId += 1; },
  methods: {
    money: formatDashboardAmount,
    rate(value) { return value === null ? 'Non calculable' : `${Number(value).toLocaleString('fr-FR', {maximumFractionDigits: 1})} %`; },
    formatDate(value) { return new Date(value).toLocaleString('fr-FR'); },
    chartOptions(categories) { return {chart: {toolbar: {show: false}}, xaxis: {categories}, dataLabels: {enabled: false}, colors: ['#7367f0', '#ea5455']}; },
    applyFilters() {
      this.validationError = '';
      if (this.mode === 'year' && (!Number.isInteger(Number(this.year)) || Number(this.year) < 1900 || Number(this.year) > 9998)) {
        this.validationError = 'Choisissez une année valide.';
        return;
      }
      if (this.mode === 'range' && (!isDashboardDate(this.from) || !isDashboardDate(this.to) || this.from > this.to)) {
        this.validationError = 'Choisissez une plage de dates valide entre 1900 et 9998.';
        return;
      }
      this.applied = this.mode === 'year' ? {year: Number(this.year)} : this.mode === 'range' ? {from: this.from, to: this.to} : {};
      this.page = 1;
      this.fetchAnalytics();
    },
    async fetchAnalytics() {
      if (!this.$can('read_dashboard', 'Statistiques')) return;
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = '';
      try {
        const report = await dashboardService.analytics({...this.applied, page: this.page});
        if (requestId === this.requestId) this.report = report;
      } catch (error) {
        if (requestId === this.requestId) this.error = dashboardErrorMessage(error, 'Impossible de charger les analyses de gestion.');
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
  },
};
</script>
<style lang="scss">
@import '@core/scss/vue/libs/chart-apex.scss';
</style>
